import { useEffect, useMemo, useRef } from 'react';

import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

/**
 * Attributes of a finished `import-hub.import` operation. The package serves
 * both data-ingestion APIs, so the flag goes here rather than into the name.
 */
export type ImportOperationContext = {
  fromTemplate: boolean;
  savedAsTemplate: boolean;
  importHubV2: boolean;
};

/**
 * Ends, in the data file wizard, the `import-hub.import` RUM operation that
 * "Create new file" or an "Import with this template" action starts before
 * navigating to `imports/new`.
 *
 * - `succeed` once the last step is submitted and the backend takes the file.
 * - `fail` when that submit, or saving the template on the way, fails. Call
 *   `restartIfEnded` before every submit: after a failure it starts a new
 *   operation described as `retry`, so each attempt is measured on its own.
 * - Unmounting while the operation is open (back link, discard, take-over,
 *   any other way out of the wizard) fails it as `abandoned`.
 *
 * Reopening an unfinished file from the imports list has no start event;
 * Datadog drops the end events of such operations.
 *
 * Mirrors `usePageOperation` from `@mint-lib/datadog`, but returns a stable
 * object: `DataFileProvider` puts the callbacks into memoized context.
 */
export function useImportOperation() {
  const isOpenRef = useRef(true);

  useEffect(() => {
    isOpenRef.current = true;

    return () => {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        failOperation('import-hub.import', 'abandoned');
      }
    };
  }, []);

  return useMemo(
    () => ({
      restartIfEnded: () => {
        if (!isOpenRef.current) {
          isOpenRef.current = true;
          startOperation('import-hub.import', { description: 'retry' });
        }
      },
      succeed: (context: ImportOperationContext) => {
        isOpenRef.current = false;
        succeedOperation('import-hub.import', { context });
      },
      fail: () => {
        isOpenRef.current = false;
        failOperation('import-hub.import', 'error');
      },
    }),
    [],
  );
}
